import React, { useState, useEffect, useMemo } from 'react';
import { Spinner, Button } from 'react-bootstrap';
import { Modal, ModalHeader } from '../../../globalComponents/ui/Modal';
import Checkbox from '../../../globalComponents/ui/Checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { getRoleCapabilities, getCapabilities, addCapabilitiesToRole, getSystemRolePermissions, updateRole } from '../services/authorizationService';
import styled from 'styled-components';

// ─────────────────────────────────────────────────────────────────────────────
// Title resolution
// ─────────────────────────────────────────────────────────────────────────────

// MODULE title: auto-derived from moduleKey — no hardcoded map needed
// e.g. "DATA_MANAGEMENT" → replace _ with space → Title Case → "Data Management"
const prettyModuleKey = (moduleKey) =>
  moduleKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

// SUBMODULE/FEATURE title: everything before the last dot, kebab-case → Title Case
// e.g. "user-management.add-location" → resource = "user-management" → "User Management"
const prettyResource = (name) =>
  name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// CAPABILITY LABEL: last segment after the final dot, kebab-case → Title Case
// e.g. "user-management.add-location" → action = "add-location" → "Add Location"
// If cap.description exists in DB, that takes priority.
const getCapLabel = (capName) => {
  const action = capName.split('.').pop();
  return action.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// ─────────────────────────────────────────────────────────────────────────────
// Schema builder: groups caps by moduleKey → resource
// ─────────────────────────────────────────────────────────────────────────────
const buildDynamicSchema = (caps) => {
  const moduleMap = {};
  for (const cap of caps) {
    const key = cap.moduleKey || 'OTHER';
    if (!moduleMap[key]) moduleMap[key] = new Set();
    const parts = cap.name.split('.');
    parts.pop();
    const resource = parts.join('.');
    if (resource) moduleMap[key].add(resource);
  }
  return Object.entries(moduleMap).map(([moduleKey, resSet]) => ({
    moduleKey,
    label: prettyModuleKey(moduleKey),
    resources: Array.from(resSet).sort(),
  }));
};

// ─────────────────────────────────────────────────────────────────────────────
// Styled components
// ─────────────────────────────────────────────────────────────────────────────

const Body = styled.div`
  padding: 20px 24px 16px;
  overflow-y: auto;
  max-height: calc(90vh - 70px);
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e2eaf0;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #1a2e38;
  outline: none;
  transition: border-color 0.2s;
  margin-bottom: 16px;
  &:focus { border-color: #3f88a5; box-shadow: 0 0 0 3px rgba(63,136,165,0.12); }
`;

const ActivePill = styled.span`
  background: #dcfce7;
  color: #166534;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 20px;
  white-space: nowrap;
`;

const TableWrap = styled.div`
  border: 1px solid #e2eaf0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  max-height: 52vh;
  overflow-y: auto;
  position: relative;
  background: #fff;
`;

/* ── Module accordion ── */
const AccordionItem = styled.div`
  border-bottom: 1px solid #e2eaf0;
  &:last-child { border-bottom: none; }
`;

const ModuleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 18px;
  background: #f0f4f8;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #3d5166;
  transition: background 0.15s;
  user-select: none;
  border-bottom: 1px solid #e2eaf0;
  position: sticky;
  top: 0;
  z-index: 9;
  &:hover { background: #e8eef4; }
`;

const CountBadge = styled.span`
  background: #fff;
  color: #3f88a5;
  font-size: 0.69rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
  text-transform: none;
  letter-spacing: normal;
  border: 1px solid #c8dfe9;
  white-space: nowrap;
`;

/* ── Feature (resource) block ── */
const FeatureBlock = styled.div`
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const FeatureHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 18px 9px 34px;
  background: #f8fafc;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.82rem;
  color: #334155;
  user-select: none;
  transition: background 0.1s;
  border-bottom: 1px solid #f1f5f9;
  &:hover { background: #f1f5f9; }
`;

const FeatureCount = styled.span`
  font-size: 0.69rem;
  font-weight: 600;
  color: ${p => p.$hasSelected ? '#3f88a5' : '#94a3b8'};
  background: ${p => p.$hasSelected ? '#e8f3f8' : '#f1f5f9'};
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
`;

/* ── Checkbox grid inside each feature ── */
const CapGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px 0;
  padding: 8px 18px 10px 48px;
  background: #fff;
`;

const CapItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  cursor: ${p => p.$disabled ? 'default' : 'pointer'};
  transition: background 0.1s;
  &:hover {
    background: ${p => p.$disabled ? 'transparent' : '#f0f7fa'};
  }
`;

const CapLabel = styled.span`
  font-size: 0.825rem;
  color: #334155;
  font-weight: 400;
  line-height: 1.3;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 40px 24px;
  color: #94a3b8;
  font-size: 0.9rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #edf2f7;
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1.5px solid #e2eaf0;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #1a2e38;
  outline: none;
  width: 240px;
  transition: border-color 0.2s;
  &:focus { border-color: #3f88a5; }
`;

const TabRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const TabGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const TabButton = styled.button`
  background: ${p => p.$active ? '#f0f7fa' : 'transparent'};
  border: 1px solid ${p => p.$active ? '#d0e6ef' : 'transparent'};
  color: ${p => p.$active ? '#3f88a5' : '#64748b'};
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
  &:hover {
    background: ${p => p.$active ? '#f0f7fa' : '#f8fafc'};
    color: ${p => p.$active ? '#3f88a5' : '#334155'};
  }
`;

const SelectAllRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  &:hover { color: #3f88a5; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const RolePermissionModal = ({ show, onHide, role, onSaveSuccess }) => {
  const [allCaps, setAllCaps] = useState([]);
  const [selectedCaps, setSelectedCaps] = useState(new Set());
  const [systemRolePermissions, setSystemRolePermissions] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [roleName, setRoleName] = useState('');
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [expandedResources, setExpandedResources] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');

  const validCaps = useMemo(() => allCaps.map(c => c.name), [allCaps]);
  const dynamicSchema = useMemo(() => buildDynamicSchema(allCaps), [allCaps]);

  useEffect(() => {
    if (show && role?.id) {
      setIsLoading(true);
      setActiveTab('all');
      setSearch('');

      Promise.all([
        getCapabilities(),
        getRoleCapabilities(role.id),
        getSystemRolePermissions(),
      ])
        .then(([allData, roleData, systemPerms]) => {
          setAllCaps(allData || []);
          setSystemRolePermissions(systemPerms || {});
          setRoleName(role?.displayName || role?.name || '');

          const schema = buildDynamicSchema(allData || []);
          setExpandedModules(new Set(schema.map(m => m.moduleKey)));
          setExpandedResources(new Set(schema.flatMap(m => m.resources)));

          const currentValidCaps = (allData || []).map(c => c.name);

          if (role?.name === 'Admin') {
            setSelectedCaps(new Set(currentValidCaps));
          } else if (systemPerms && systemPerms[role?.name]) {
            const allowed = systemPerms[role.name];
            setSelectedCaps(
              allowed.includes('*')
                ? new Set(currentValidCaps)
                : new Set(allowed.filter(p => currentValidCaps.includes(p)))
            );
          } else {
            setSelectedCaps(new Set((roleData || []).map(c => c.name)));
          }
        })
        .catch(e => console.error('Error fetching capabilities', e))
        .finally(() => setIsLoading(false));
    } else {
      setSelectedCaps(new Set());
    }
  }, [show, role]);

  const isSystemRole = Object.keys(systemRolePermissions).includes(role?.name);

  // ── Toggle helpers ────────────────────────────────────────────────────────

  const handleToggleCap = (capName) => {
    if (isSystemRole) return;
    const s = new Set(selectedCaps);
    s.has(capName) ? s.delete(capName) : s.add(capName);
    setSelectedCaps(s);
  };

  const isAllSelected = validCaps.length > 0 && validCaps.every(c => selectedCaps.has(c));
  const handleToggleAll = () => {
    if (isSystemRole) return;
    setSelectedCaps(isAllSelected ? new Set() : new Set(validCaps));
  };

  const getModCaps = (mod) =>
    mod.resources.flatMap(res =>
      allCaps.filter(c => { const p = c.name.split('.'); p.pop(); return p.join('.') === res; }).map(c => c.name)
    );

  const getResCaps = (resource) =>
    allCaps
      .filter(c => { const p = c.name.split('.'); p.pop(); return p.join('.') === resource; })
      .map(c => c.name);

  const handleToggleModule = (mod) => {
    if (isSystemRole) return;
    const modCaps = getModCaps(mod);
    const allChecked = modCaps.every(c => selectedCaps.has(c));
    const s = new Set(selectedCaps);
    if (allChecked) modCaps.forEach(c => s.delete(c));
    else modCaps.forEach(c => s.add(c));
    setSelectedCaps(s);
  };

  const handleToggleResource = (resource) => {
    if (isSystemRole) return;
    const resCaps = getResCaps(resource);
    const allChecked = resCaps.length > 0 && resCaps.every(c => selectedCaps.has(c));
    const s = new Set(selectedCaps);
    if (allChecked) resCaps.forEach(c => s.delete(c));
    else resCaps.forEach(c => s.add(c));
    setSelectedCaps(s);
  };

  const toggleExpandModule = (key) => {
    const s = new Set(expandedModules);
    s.has(key) ? s.delete(key) : s.add(key);
    setExpandedModules(s);
  };

  const toggleExpandResource = (res) => {
    const s = new Set(expandedResources);
    s.has(res) ? s.delete(res) : s.add(res);
    setExpandedResources(s);
  };

  // ── Filtering ─────────────────────────────────────────────────────────────

  const filteredSchema = useMemo(() => {
    if (!search.trim()) return dynamicSchema;
    const q = search.toLowerCase();
    return dynamicSchema
      .map(mod => {
        const modMatch = mod.label.toLowerCase().includes(q);
        const filteredRes = mod.resources.filter(res =>
          modMatch ||
          prettyResource(res).toLowerCase().includes(q) ||
          allCaps.some(c => {
            const p = c.name.split('.'); p.pop();
            return p.join('.') === res && (c.description || getCapLabel(c.name)).toLowerCase().includes(q);
          })
        );
        return { ...mod, resources: filteredRes };
      })
      .filter(mod => mod.resources.length > 0);
  }, [search, dynamicSchema, allCaps]);

  const displayedSchema = useMemo(() => {
    if (activeTab === 'all') return filteredSchema;
    return filteredSchema
      .map(mod => {
        const filteredRes = mod.resources.filter(res =>
          allCaps.some(c => {
            const p = c.name.split('.'); p.pop();
            return p.join('.') === res && selectedCaps.has(c.name);
          })
        );
        return filteredRes.length ? { ...mod, resources: filteredRes } : null;
      })
      .filter(Boolean);
  }, [activeTab, filteredSchema, allCaps, selectedCaps]);

  // ── Save ─────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!roleName.trim()) { alert('Role name is required.'); return; }
    setIsSaving(true);
    try {
      const originalName = role?.displayName || role?.name || '';
      if (roleName.trim() !== originalName) {
        const payload = isSystemRole ? { displayName: roleName.trim() } : { name: roleName.trim() };
        await updateRole(role.id, payload);
      }
      if (!isSystemRole) {
        const selectedIds = allCaps.filter(c => selectedCaps.has(c.name)).map(c => c.id);
        await addCapabilitiesToRole({ roleId: role.id, capabilityIds: selectedIds });
      }
      onSaveSuccess?.();
      onHide();
    } catch (e) {
      console.error('Failed to save role updates', e);
      alert(e?.message || 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <Modal show={show} onClose={onHide} maxWidth={820}>
      <ModalHeader
        title={`Manage Role — ${role?.displayName || role?.name || ''}`}
        subtitle={
          isSystemRole
            ? 'System predefined role · View default permissions and edit display name'
            : 'Manage role name and capabilities'
        }
        onClose={onHide}
      />

      <Body>
        {/* ── Top bar ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '18px', paddingBottom: '14px', borderBottom: '1px solid #edf2f7', gap: '20px' }}>
          <div style={{ flex: 1, maxWidth: '380px' }}>
            <FormLabel style={{ marginBottom: '4px' }}>Role Name *</FormLabel>
            <FormInput
              placeholder="Role Name"
              value={roleName}
              onChange={e => setRoleName(e.target.value)}
              disabled={isSaving}
              style={{ marginBottom: 0 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <SearchInput
              placeholder="Search permissions…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <ActivePill>● Active</ActivePill>
          </div>
        </div>

        {/* ── Tab row ── */}
        <TabRow>
          <TabGroup>
            <TabButton $active={activeTab === 'all'} onClick={() => setActiveTab('all')} type="button">
              All Permissions
            </TabButton>
            <TabButton $active={activeTab === 'selected'} onClick={() => setActiveTab('selected')} type="button">
              Selected ({selectedCaps.size})
            </TabButton>
          </TabGroup>
          {!isSystemRole && (
            <SelectAllRow onClick={handleToggleAll}>
              <Checkbox checked={isAllSelected} onChange={handleToggleAll} disabled={isSystemRole} />
              <span>Select All</span>
            </SelectAllRow>
          )}
        </TabRow>

        {/* ── Permission tree ── */}
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 24px' }}>
            <Spinner animation="border" style={{ color: '#3f88a5' }} />
            <p style={{ marginTop: 12, color: '#6b8794', fontSize: '0.875rem' }}>Loading permissions…</p>
          </div>
        ) : (
          <TableWrap>
            {displayedSchema.length === 0 ? (
              <EmptyMsg>
                {activeTab === 'selected'
                  ? search ? 'No selected permissions match your search.' : 'No permissions selected for this role yet.'
                  : 'No permissions match your search.'}
              </EmptyMsg>
            ) : (
              displayedSchema.map(mod => {
                const modCaps = getModCaps(mod);
                const isModSelected = modCaps.length > 0 && modCaps.every(c => selectedCaps.has(c));
                const isModExpanded = expandedModules.has(mod.moduleKey);
                const selCount = modCaps.filter(c => selectedCaps.has(c)).length;

                return (
                  <AccordionItem key={mod.moduleKey}>
                    {/* ── Module header ── */}
                    <ModuleHeader onClick={() => toggleExpandModule(mod.moduleKey)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FontAwesomeIcon
                          icon={isModExpanded ? faChevronDown : faChevronRight}
                          style={{ color: '#5a7a91', fontSize: '0.75rem', width: '12px' }}
                        />
                        {!isSystemRole && (
                          <span onClick={e => { e.stopPropagation(); handleToggleModule(mod); }}>
                            <Checkbox checked={isModSelected} onChange={() => handleToggleModule(mod)} disabled={isSystemRole} />
                          </span>
                        )}
                        <span>{mod.label}</span>
                      </div>
                      <CountBadge>{selCount} / {modCaps.length} Selected</CountBadge>
                    </ModuleHeader>

                    {/* ── Resources ── */}
                    {isModExpanded && mod.resources.map(resource => {
                      const resCaps = allCaps.filter(c => {
                        const p = c.name.split('.'); p.pop(); return p.join('.') === resource;
                      });
                      const visibleCaps = activeTab === 'selected'
                        ? resCaps.filter(c => selectedCaps.has(c.name))
                        : resCaps;

                      if (visibleCaps.length === 0) return null;

                      const isResSelected = resCaps.length > 0 && resCaps.every(c => selectedCaps.has(c.name));
                      const isResExpanded = expandedResources.has(resource);
                      const resSelCount = resCaps.filter(c => selectedCaps.has(c.name)).length;
                      const hasSelected = resSelCount > 0;

                      return (
                        <FeatureBlock key={resource}>
                          {/* ── Feature header ── */}
                          <FeatureHeader onClick={() => toggleExpandResource(resource)}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <FontAwesomeIcon
                                icon={isResExpanded ? faChevronDown : faChevronRight}
                                style={{ color: '#94a3b8', fontSize: '0.68rem', width: '10px' }}
                              />
                              {!isSystemRole && (
                                <span onClick={e => { e.stopPropagation(); handleToggleResource(resource); }}>
                                  <Checkbox checked={isResSelected} onChange={() => handleToggleResource(resource)} disabled={isSystemRole} />
                                </span>
                              )}
                              <span>{prettyResource(resource)}</span>
                            </div>
                            <FeatureCount $hasSelected={hasSelected}>
                              {resSelCount} / {resCaps.length}
                            </FeatureCount>
                          </FeatureHeader>

                          {/* ── Capability grid (3 per row) ── */}
                          {isResExpanded && (
                            <CapGrid>
                              {visibleCaps.map(cap => {
                                const isGranted = selectedCaps.has(cap.name);
                                const label = cap.description || getCapLabel(cap.name);
                                return (
                                  <CapItem
                                    key={cap.name}
                                    $disabled={isSystemRole}
                                    onClick={() => handleToggleCap(cap.name)}
                                  >
                                    <Checkbox
                                      checked={isGranted}
                                      onChange={() => handleToggleCap(cap.name)}
                                      disabled={isSystemRole}
                                    />
                                    <CapLabel>{label}</CapLabel>
                                  </CapItem>
                                );
                              })}
                            </CapGrid>
                          )}
                        </FeatureBlock>
                      );
                    })}
                  </AccordionItem>
                );
              })
            )}
          </TableWrap>
        )}

        {!isLoading && (
          <Footer>
            <Button variant="outline-secondary" onClick={onHide} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              style={{ backgroundColor: '#3f88a5', borderColor: '#3f88a5' }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save Changes'}
            </Button>
          </Footer>
        )}
      </Body>
    </Modal>
  );
};

export default RolePermissionModal;
